// ---------------------------------------------------------------------------
// Issue domain types
// Ported from trusstor_dashboard/shared/interfaces/IIssueShared.ts
//             trusstor_dashboard/shared/interfaces/IIssue.ts
// ---------------------------------------------------------------------------

import { z } from 'zod';
import { dateField } from './_shared.js';
import { baseAreaSchema, baseFloorSchema } from './floor.js';
import { minimizedProfessionSchema } from './profession.js';

// ---------------------------------------------------------------------------
// IssueCommentType
// (from trusstor_dashboard/shared/interfaces/ICommentShared.ts)
// ---------------------------------------------------------------------------

export enum IssueCommentType {
	IMAGE = 'image',
	TEXT = 'text',
	PDF = 'pdf',
}

// ---------------------------------------------------------------------------
// IssuePriority
// (from trusstor_dashboard/shared/interfaces/IIssueShared.ts)
// ---------------------------------------------------------------------------

export enum IssuePriority {
	CRITICAL = 'critical',
	REGULAR = 'regular',
}

// ---------------------------------------------------------------------------
// IssueStatus
// (from trusstor_dashboard/shared/interfaces/IIssueShared.ts)
// ---------------------------------------------------------------------------

export enum IssueStatus {
	PLANNED = 'planned',
	FOR_REVIEW = 'forReview',
	REJECTED = 'rejected',
	COMPLETED = 'completed',
}

// ---------------------------------------------------------------------------
// ISelectedPoint
// (from trusstor_dashboard/shared/interfaces/ISelectedPoint.ts)
// ---------------------------------------------------------------------------

export interface ISelectedPoint {
	xPercentage: number;
	yPercentage: number;
}

export interface ISelectedPointWithAreaId extends ISelectedPoint {
	areaId: string;
}

// ---------------------------------------------------------------------------
// selectedPointSchema
// ---------------------------------------------------------------------------

const selectedPointSchema = z.object({
	xPercentage: z.number(),
	yPercentage: z.number(),
}).passthrough();

// ---------------------------------------------------------------------------
// areaWithSelectedPointsSchema
// ---------------------------------------------------------------------------

const areaWithSelectedPointsSchema = baseAreaSchema.extend({
	selectedPoints: z.array(selectedPointSchema).optional(),
});

// ---------------------------------------------------------------------------
// issueLocationSchema / IIssueLocation
// ---------------------------------------------------------------------------

export const issueLocationSchema = z
	.object({
		area: areaWithSelectedPointsSchema,
		floor: baseFloorSchema,
	})
	.passthrough();

export interface IAreaWithSelectedPoints {
	areaId: string;
	areaNick: string;
	selectedPoints?: ISelectedPoint[];
}

export interface IIssueLocation {
	area: IAreaWithSelectedPoints;
	floor: { floorId: string; floorNick: string };
}

// ---------------------------------------------------------------------------
// userIdentitySchema
// ---------------------------------------------------------------------------

const userIdentitySchema = z.object({
	name: z.string(),
	username: z.string(),
}).passthrough();

// ---------------------------------------------------------------------------
// issueCommentSchema
// ---------------------------------------------------------------------------

export const issueCommentSchema = z
	.object({
		commentId: z.string(),
		dateCreated: dateField,
		sender: userIdentitySchema,
		content: z.object({
			type: z.enum(IssueCommentType),
			value: z.string(),
			sizeInBytes: z.number().nullable().optional(),
			filename: z.string().nullable().optional(),
		}).passthrough(),
	})
	.passthrough();

// ---------------------------------------------------------------------------
// issueAssigneeSchema / IIssueAssignee
// ---------------------------------------------------------------------------

export const issueAssigneeSchema = userIdentitySchema
	.extend({ name: z.string().optional() })
	.passthrough();

export type IIssueAssignee = z.infer<typeof issueAssigneeSchema>;

// ---------------------------------------------------------------------------
// issueSchema / IIssueShared
// (from trusstor_dashboard/shared/interfaces/IIssueShared.ts)
// ---------------------------------------------------------------------------

export const issueSchema = z
	.object({
		_id: z.string(),
		createdBy: userIdentitySchema.optional(),
		statusUpdateUser: userIdentitySchema.optional(),
		projectId: z.string(),
		status: z.enum(IssueStatus),
		createDate: dateField,
		description: z.string(),
		dueDate: dateField.optional(),
		profession: minimizedProfessionSchema.optional(),
		priority: z.enum(IssuePriority),
		locations: z.array(issueLocationSchema).optional(),
		assignee: issueAssigneeSchema.nullable().optional(),
		comments: z.array(issueCommentSchema).optional(),
		statusUpdateDate: dateField.optional(),
		isInitial: z.boolean().optional(),
		linkedAreaSequenceItemId: z.string().nullable().optional(),
		linkedActivityGroupId: z.string().nullable().optional(),
		isPrivate: z.boolean().optional(),
		procoreId: z.union([z.number(), z.string()]).nullable().optional(),
		isSafety: z.boolean().optional(),
	})
	.passthrough();

export type IIssueShared = z.infer<typeof issueSchema>;

// ---------------------------------------------------------------------------
// IIssue
// (from trusstor_dashboard/shared/interfaces/IIssue.ts)
// IIssue = IIssueShared (type alias for backward compatibility)
// ---------------------------------------------------------------------------

export type IIssue = IIssueShared;

export type ICreateIssue = Omit<IIssue, '_id' | 'createDate' | 'assignee'> & {
	assignee?: {
		username: string;
	} | null;
};

export interface IUpdateIssue extends Omit<IIssue, 'profession' | 'locations' | 'assignee'> {
	profession?: IIssue['profession'] | null;
	assignee?: IIssue['assignee'] | null;
	locations: IIssue['locations'] | null;
}

export interface IBulkReassignRequest {
	assignments: { [issueId: string]: string };
}

export interface IBulkReassignResponse {
	updated: number;
}
